import { useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { apiError } from "@/lib/api";
import { PageHeader, Loading, EmptyState } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CalendarClock, AlertTriangle, ArrowLeftRight, Trash2, History } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Cell } from "recharts";

// 0=Monday..6=Sunday (Python date.weekday() convention, matches the backend).
const WEEKDAYS = [
  { v: 0, label: "Monday", short: "Mon" }, { v: 1, label: "Tuesday", short: "Tue" },
  { v: 2, label: "Wednesday", short: "Wed" }, { v: 3, label: "Thursday", short: "Thu" },
  { v: 4, label: "Friday", short: "Fri" }, { v: 5, label: "Saturday", short: "Sat" },
  { v: 6, label: "Sunday", short: "Sun" },
];

// An employee with no weekly_off_day assigned yet behaves as Sunday (matching
// the backend's default), but is flagged throughout this page so HR can find
// and reassign them to spread coverage across the week.
const effectiveDay = (e) => e.employment?.weekly_off_day ?? 6;

const todayIST = () => new Date(Date.now() + 5.5 * 3600 * 1000).toISOString().slice(0, 10);

const fmtDate = (s) => (s ? new Date(`${s}T00:00:00`).toLocaleDateString("en-IN", { weekday: "short", day: "2-digit", month: "short", year: "numeric" }) : "—");

// Week-off swap: the employee worked their weekly off and took another day off instead.
// Payroll then pays the day off as the weekly off (Super Admin only).
function WeekOffSwaps({ employees }) {
  const qc = useQueryClient();
  const [form, setForm] = useState({ employee_id: "", worked_date: "", off_date: "", reason: "" });
  const { data: swaps } = useQuery({ queryKey: ["weekly-off-swaps"], queryFn: async () => (await api.get("/weekly-off-swaps")).data });
  const create = useMutation({
    mutationFn: async () => (await api.post("/weekly-off-swaps", form)).data,
    onSuccess: () => {
      toast.success("Swap saved. Recalculate payroll for that month to reflect it.");
      setForm({ employee_id: "", worked_date: "", off_date: "", reason: "" });
      qc.invalidateQueries({ queryKey: ["weekly-off-swaps"] });
    },
    onError: (e) => toast.error(apiError(e.response?.data?.detail)),
  });
  const remove = useMutation({
    mutationFn: async (id) => (await api.delete(`/weekly-off-swaps/${id}`)).data,
    onSuccess: () => { toast.success("Swap removed"); qc.invalidateQueries({ queryKey: ["weekly-off-swaps"] }); },
    onError: (e) => toast.error(apiError(e.response?.data?.detail)),
  });
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target ? e.target.value : e }));
  const ready = form.employee_id && form.worked_date && form.off_date && form.reason.trim().length >= 3;
  const sortedEmps = [...(employees || [])].sort((a, b) => (a.identity?.name || "").localeCompare(b.identity?.name || ""));

  return (
    <Card className="border-slate-200 shadow-none overflow-hidden mt-6" data-testid="weekoff-swaps">
      <div className="p-4 border-b border-slate-100">
        <h3 className="font-serif-display text-lg text-navy flex items-center gap-2"><ArrowLeftRight className="h-4 w-4" /> Week-off Swaps</h3>
        <p className="text-sm text-slate-500 mt-1">Employee worked their weekly off and took another day off within the same week. The day off is paid as the weekly off, and the worked day cannot also earn a comp-off.
          If the employee has moved to a different weekly-off day for good, change their weekly off above with a start date instead of recording a swap every week.</p>
      </div>
      <div className="p-4 grid gap-3 md:grid-cols-5 items-end border-b border-slate-100">
        <div>
          <label className="text-xs text-slate-500">Employee</label>
          <Select value={form.employee_id} onValueChange={set("employee_id")}>
            <SelectTrigger data-testid="swap-employee"><SelectValue placeholder="Select employee" /></SelectTrigger>
            <SelectContent className="bg-white">
              {sortedEmps.map((e) => <SelectItem key={e.id} value={e.id}>{e.identity?.name} ({WEEKDAYS[effectiveDay(e)].short} off)</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div><label className="text-xs text-slate-500">Worked weekly off on</label><Input type="date" value={form.worked_date} onChange={set("worked_date")} data-testid="swap-worked" /></div>
        <div><label className="text-xs text-slate-500">Day off taken instead</label><Input type="date" value={form.off_date} onChange={set("off_date")} data-testid="swap-off" /></div>
        <div><label className="text-xs text-slate-500">Reason</label><Input value={form.reason} onChange={set("reason")} placeholder="e.g. Covered Saturday rush" data-testid="swap-reason" /></div>
        <Button disabled={!ready || create.isPending} onClick={() => create.mutate()} data-testid="swap-save">Save swap</Button>
      </div>
      {(swaps || []).length === 0 ? (
        <p className="text-sm text-slate-400 p-6 text-center">No swaps recorded.</p>
      ) : (
        <Table>
          <TableHeader><TableRow className="bg-slate-50">
            <TableHead className="overline text-slate-500">Employee</TableHead>
            <TableHead className="overline text-slate-500">Worked weekly off</TableHead>
            <TableHead className="overline text-slate-500">Day off instead</TableHead>
            <TableHead className="overline text-slate-500">Reason</TableHead>
            <TableHead />
          </TableRow></TableHeader>
          <TableBody>
            {swaps.map((s) => (
              <TableRow key={s.id}>
                <TableCell className="font-medium text-navy">{s.employee_name}<span className="text-slate-400 text-xs font-mono-i ml-2">{s.employee_code}</span></TableCell>
                <TableCell>{fmtDate(s.worked_date)}</TableCell>
                <TableCell>{fmtDate(s.off_date)}</TableCell>
                <TableCell className="text-slate-600">{s.reason}</TableCell>
                <TableCell className="text-right">
                  <Button variant="ghost" size="sm" onClick={() => remove.mutate(s.id)} data-testid={`swap-delete-${s.id}`}><Trash2 className="h-4 w-4 text-slate-500" /></Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </Card>
  );
}

// Every weekly-off change, with the date it took effect. Payroll uses the day that
// was in force on each date, so a change never rewrites months already worked.
function WeeklyOffChanges() {
  const { data: changes } = useQuery({ queryKey: ["weekly-off-changes"], queryFn: async () => (await api.get("/weekly-off-changes")).data });
  return (
    <Card className="border-slate-200 shadow-none overflow-hidden mt-6" data-testid="weekoff-changes">
      <div className="p-4 border-b border-slate-100">
        <h3 className="font-serif-display text-lg text-navy flex items-center gap-2"><History className="h-4 w-4" /> Weekly-off Changes</h3>
        <p className="text-sm text-slate-500 mt-1">Each change applies from its start date. Earlier dates keep the old weekly off, in payroll and leave.</p>
      </div>
      {(changes || []).length === 0 ? (
        <p className="text-sm text-slate-400 p-6 text-center">No changes recorded yet.</p>
      ) : (
        <Table>
          <TableHeader><TableRow className="bg-slate-50">
            <TableHead className="overline text-slate-500">Employee</TableHead>
            <TableHead className="overline text-slate-500">From</TableHead>
            <TableHead className="overline text-slate-500">Change</TableHead>
            <TableHead className="overline text-slate-500">Reason</TableHead>
            <TableHead className="overline text-slate-500">By</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {changes.map((c) => (
              <TableRow key={c.id}>
                <TableCell className="font-medium text-navy">{c.employee_name}<span className="text-slate-400 text-xs font-mono-i ml-2">{c.employee_code}</span></TableCell>
                <TableCell>{fmtDate(c.effective_from)}</TableCell>
                <TableCell>{WEEKDAYS[c.previous_day]?.label} → <strong>{WEEKDAYS[c.new_day]?.label}</strong></TableCell>
                <TableCell className="text-slate-600">{c.reason || "—"}</TableCell>
                <TableCell className="text-slate-500 text-xs">{c.created_by}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </Card>
  );
}

export default function WeeklyOffRoster() {
  const qc = useQueryClient();
  const [deptFilter, setDeptFilter] = useState("all");

  const { data: employees, isLoading } = useQuery({
    queryKey: ["employees-weekly-off"],
    queryFn: async () => (await api.get("/employees", { params: { status: "Active" } })).data,
  });
  const { data: depts } = useQuery({ queryKey: ["departments"], queryFn: async () => (await api.get("/departments")).data });

  const { isSuperAdmin } = useAuth();
  // Pending change: { emp, day, effective_from, reason } while the dialog is open.
  const [pending, setPending] = useState(null);
  const updateWeeklyOff = useMutation({
    mutationFn: async ({ emp, day, effective_from, reason }) =>
      (await api.post(`/employees/${emp.id}/weekly-off`, { weekly_off_day: day, effective_from, reason })).data,
    onSuccess: (_r, v) => {
      toast.success(v.effective_from < todayIST()
        ? "Weekly off changed. Recalculate payroll for the months it affects."
        : "Weekly off updated");
      setPending(null);
      qc.invalidateQueries({ queryKey: ["employees-weekly-off"] });
      qc.invalidateQueries({ queryKey: ["weekly-off-changes"] });
    },
    onError: (e) => toast.error(apiError(e.response?.data?.detail)),
  });

  const list = employees || [];
  const total = list.length;

  const counts = useMemo(() => WEEKDAYS.map((d) => ({
    ...d, count: list.filter((e) => effectiveDay(e) === d.v).length,
  })), [list]);

  const unassignedCount = list.filter((e) => e.employment?.weekly_off_day == null).length;
  const maxCount = total ? Math.max(...counts.map((c) => c.count)) : 0;
  const maxShare = total ? maxCount / total : 0;
  const overloadedDay = maxShare > 0.4 ? counts.find((c) => c.count === maxCount) : null;

  const filtered = deptFilter === "all" ? list : list.filter((e) => e.employment?.department_id === deptFilter);
  const sorted = [...filtered].sort((a, b) =>
    effectiveDay(a) - effectiveDay(b) || (a.identity?.name || "").localeCompare(b.identity?.name || ""));

  return (
    <div>
      <PageHeader overline="Attendance & Leave" title="Weekly Off Roster" icon={CalendarClock}
        subtitle="As a diagnostics lab running 7 days a week, staff are staggered across different weekly-off days rather than everyone taking Sunday off." />

      {isLoading ? <Loading /> : (
        <>
          {(unassignedCount > 0 || overloadedDay) && (
            <Card className="p-4 border-amber-200 bg-amber-50 shadow-none mb-6 flex items-start gap-3" data-testid="roster-warning">
              <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5 shrink-0" />
              <div className="text-sm text-amber-800 space-y-1">
                {unassignedCount > 0 && (
                  <p><strong>{unassignedCount}</strong> active employee{unassignedCount === 1 ? "" : "s"} {unassignedCount === 1 ? "has" : "have"} no weekly-off day assigned yet — defaulting to Sunday. Assign a day below to spread lab coverage.</p>
                )}
                {overloadedDay && (
                  <p><strong>{overloadedDay.label}</strong> is the weekly off for {overloadedDay.count} of {total} active staff ({Math.round(maxShare * 100)}%) — consider moving some of them to other days for balanced coverage.</p>
                )}
              </div>
            </Card>
          )}

          <Card className="p-6 border-slate-200 shadow-none mb-6">
            <h3 className="font-serif-display text-xl text-navy mb-1">Headcount Off, by Day</h3>
            <p className="text-sm text-slate-500 mb-4">How many active employees are scheduled off on each day of the week.</p>
            {total === 0 ? (
              <p className="text-sm text-slate-400 py-16 text-center">No active employees yet.</p>
            ) : (
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={counts} margin={{ left: -18, right: 8 }}>
                  <XAxis dataKey="short" tick={{ fontSize: 12, fill: "#64748b" }} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#64748b" }} />
                  <Tooltip cursor={{ fill: "rgba(10,143,143,0.06)" }} contentStyle={{ borderRadius: 8, border: "1px solid #e2e8f0", fontSize: 13 }}
                    formatter={(v, _n, p) => [`${v} employee${v === 1 ? "" : "s"}`, p.payload.label]} />
                  <Bar dataKey="count" radius={[6, 6, 0, 0]} maxBarSize={54}>
                    {counts.map((c, i) => <Cell key={i} fill={total && c.count / total > 0.4 ? "#d97706" : "#0a8f8f"} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </Card>

          <Card className="border-slate-200 shadow-none overflow-hidden">
            <div className="flex flex-wrap items-center justify-between gap-3 p-4 border-b border-slate-100">
              <h3 className="font-serif-display text-lg text-navy">Assign / Review by Employee</h3>
              <Select value={deptFilter} onValueChange={setDeptFilter}>
                <SelectTrigger className="w-56" data-testid="roster-dept-filter"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="all">All Departments</SelectItem>
                  {(depts || []).map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {sorted.length === 0 ? (
              <EmptyState icon={CalendarClock} title="No active employees" hint="Add employees in the Employee Master to assign weekly-off days." />
            ) : (
              <Table>
                <TableHeader><TableRow className="bg-slate-50">
                  <TableHead className="overline text-slate-500">Employee</TableHead>
                  <TableHead className="overline text-slate-500">Department</TableHead>
                  <TableHead className="overline text-slate-500">Designation</TableHead>
                  <TableHead className="overline text-slate-500">Weekly Off</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {sorted.map((e) => (
                    <TableRow key={e.id} className="table-row-i" data-testid={`roster-row-${e.employee_id}`}>
                      <TableCell className="font-medium text-navy">{e.identity?.name}<span className="text-slate-400 text-xs font-mono-i ml-2">{e.employee_id}</span></TableCell>
                      <TableCell className="text-slate-600">{e.department_name || "—"}</TableCell>
                      <TableCell className="text-slate-600">{e.designation_title || "—"}</TableCell>
                      <TableCell>
                        <Select value={String(effectiveDay(e))} onValueChange={(v) => setPending({ emp: e, day: parseInt(v, 10), effective_from: todayIST(), reason: "" })}>
                          <SelectTrigger className={`w-40 ${e.employment?.weekly_off_day == null ? "border-amber-300 text-amber-700" : ""}`} data-testid={`roster-weekly-off-${e.employee_id}`}><SelectValue /></SelectTrigger>
                          <SelectContent className="bg-white">
                            {WEEKDAYS.map((d) => <SelectItem key={d.v} value={String(d.v)}>{d.label}</SelectItem>)}
                          </SelectContent>
                        </Select>
                        {e.employment?.weekly_off_day == null && <p className="text-[11px] text-amber-600 mt-1">Not yet assigned — showing default</p>}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </Card>

          <WeeklyOffChanges />
          <WeekOffSwaps employees={list} />
        </>
      )}

      <Dialog open={!!pending} onOpenChange={(o) => !o && setPending(null)}>
        <DialogContent className="bg-white max-w-md" data-testid="weekoff-change-dialog">
          {pending && (
            <>
              <DialogHeader>
                <DialogTitle className="font-serif-display text-xl text-navy">Change weekly off</DialogTitle>
              </DialogHeader>
              <p className="text-sm text-slate-600">
                <strong>{pending.emp.identity?.name}</strong>: {WEEKDAYS[effectiveDay(pending.emp)].label} → <strong>{WEEKDAYS[pending.day].label}</strong>
              </p>
              <div className="space-y-1">
                <label className="text-xs text-slate-500">Starts from</label>
                <Input type="date" value={pending.effective_from} max={todayIST()} min={isSuperAdmin ? undefined : todayIST()}
                  onChange={(ev) => setPending((p) => ({ ...p, effective_from: ev.target.value }))} data-testid="weekoff-change-from" />
                <p className="text-xs text-slate-500">
                  Days before this date keep the old weekly off.
                  {isSuperAdmin ? " Back-date it if the employee had already switched (payroll for those months must be recalculated)." : " Only Super Admin can back-date a change."}
                </p>
              </div>
              <div className="space-y-1">
                <label className="text-xs text-slate-500">Reason (optional)</label>
                <Input value={pending.reason} onChange={(ev) => setPending((p) => ({ ...p, reason: ev.target.value }))}
                  placeholder="e.g. Moved to Sunday on request" data-testid="weekoff-change-reason" />
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setPending(null)}>Cancel</Button>
                <Button disabled={!pending.effective_from || updateWeeklyOff.isPending}
                  onClick={() => updateWeeklyOff.mutate(pending)} data-testid="weekoff-change-save">Save change</Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
